/**
 * ReopenEligibleBanner
 * CCH Care — Late Documentation Phase 2 surface (2026-05-22)
 *
 * Sibling of PendingCoSignBanner. Surfaces ADL records that closed
 * incomplete (past grace, < 9/9 counting N/A) but are still within
 * the 24-hour supervisor-reopen cutoff. Tapping a row deep-links to
 * the flow sheet, where the existing ADLReopenModal handles the
 * action via the closed-window banner's Reopen CTA.
 *
 * Color choice: warning amber rather than primary blue. PendingCoSign
 * is normal-flow ("records waiting for routine review"); this is a
 * gap surface ("records that didn't finish on time"). Amber matches
 * the grace-period banner already shown on the flow sheet. When at
 * least one row is within 2 hours of the 24-hour cutoff, the banner
 * darkens to danger red — the action becomes time-pressured.
 *
 * Renders only for supervisors AND only when at least one record is
 * pending — otherwise null. Backend role-gates as well; this is
 * defense in depth.
 *
 * v1 scope (per 2026-05-22 design decision): strict — only records
 * still within the 24-hour reopen cutoff. Phase 3 historicals
 * (>24h past shift end) belong to the amendment flow and don't
 * appear here. Expired-reopen records that are still under 24h are
 * INCLUDED with a "Previously reopened" pill — second-chance reopen.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getReopenEligible } from '../../../api/adl';
import { COLORS } from '../../../config/constants';

const SUPERVISOR_ROLES = ['Care Supervisor', 'Care Admin'];
const URGENT_THRESHOLD_MIN = 2 * 60; // Switch to danger color when ≤2h to cutoff

export default function ReopenEligibleBanner({ role }) {
  const navigate = useNavigate();
  const [records, setRecords]   = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading]   = useState(false);

  const isSupervisor = SUPERVISOR_ROLES.includes(role);

  useEffect(() => {
    if (!isSupervisor) return;
    setLoading(true);
    getReopenEligible(2)
      .then(d => { if (d.success) setRecords(d.records ?? []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isSupervisor]);

  if (!isSupervisor || (!loading && records.length === 0)) return null;
  if (loading && records.length === 0) return null; // silent on first load

  // Banner shifts amber → red when any row is within 2h of the
  // 24-hour cutoff. Conveys urgency at a glance.
  const hasUrgent   = records.some(r => r.minutes_until_cutoff <= URGENT_THRESHOLD_MIN);
  const accent      = hasUrgent ? COLORS.danger      : COLORS.warning;
  const accentLight = hasUrgent ? COLORS.dangerLight : COLORS.warningLight;

  return (
    <div style={{
      margin: '12px 16px',
      background: accentLight,
      border: `1px solid ${accent}`,
      borderRadius: 12,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={expanded}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }} aria-hidden="true">⏱️</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: accent }}>
              {records.length} record{records.length === 1 ? '' : 's'} need
              {records.length === 1 ? 's' : ''} late doc review
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>
              {hasUrgent
                ? 'Some are near the 24-hour reopen cutoff.'
                : 'Past grace, within reopen window.'}
              {' '}Tap to {expanded ? 'collapse' : 'review'}.
            </div>
          </div>
        </div>
        <span style={{ fontSize: 14, color: accent }} aria-hidden="true">
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      {expanded && (
        <div style={{
          borderTop: `1px solid ${accent}`,
          background: COLORS.surface,
        }}>
          {records.map(r => (
            <ReopenEligibleRow key={r.adl_record_id} record={r} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReopenEligibleRow({ record, navigate }) {
  const name = `${record.patient_first_name ?? ''} ${record.patient_last_name ?? ''}`
    .trim() || `Patient ${record.patient_id}`;
  const dateStr = formatDate(record.shift_date);

  const urgent = record.minutes_until_cutoff <= URGENT_THRESHOLD_MIN;

  function open() {
    // Deep-link to flow sheet. The closed-window banner there renders
    // the supervisor-only Reopen CTA (shipped 2026-05-21) which opens
    // ADLReopenModal — same action surface, just reached via this list.
    const url = `/dashboard/adl/${record.patient_id}`
      + `?shift_id=${record.care_shift_id}`
      + `&shift_type=${record.shift_type}`;
    navigate(url);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: COLORS.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
        </div>
        <div style={{
          fontSize: 12, color: COLORS.textMuted, marginTop: 2,
          display: 'flex', alignItems: 'center', flexWrap: 'wrap',
          columnGap: 6, rowGap: 4,
        }}>
          <span>
            {record.shift_type} Shift &middot; {dateStr}
            {' · '}
            <strong style={{ color: COLORS.text }}>{record.done_count}/9</strong>
            {' done'}
          </span>
          <span style={{
            color: urgent ? COLORS.danger : COLORS.warning,
            fontWeight: 600,
          }}>
            {formatRemaining(record.minutes_until_cutoff)}
          </span>
          {record.was_previously_reopened && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: COLORS.textMuted,
              background: COLORS.bg,
              border: `1px solid ${COLORS.border}`,
              padding: '2px 8px', borderRadius: 12,
              whiteSpace: 'nowrap',
            }}>
              Previously reopened
            </span>
          )}
        </div>
      </div>
      <span
        style={{ fontSize: 18, color: COLORS.primary, marginLeft: 12 }}
        aria-hidden="true"
      >
        →
      </span>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatRemaining(minutes) {
  if (minutes == null || minutes <= 0) return 'expired';
  if (minutes < 60) return `${minutes}m left`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h ${mins}m left` : `${hrs}h left`;
}